import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTabPermissions } from '../../context/TabPermissionContext';
import { UserRole } from '../../constants/userRole';
import { ApprovalStatus } from '../../constants/approvalStatus';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Clock,
  RefreshCw,
  Users,
  BarChart3,
  HelpCircle,
  X,
  ClipboardCheck,
  UserCheck,
  Network,
  ListTodo,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Compass,
  Briefcase,
  Shield,
  Brain,
  Eye,
  Mail,
} from 'lucide-react';

const Sidebar = ({ isOpen, onClose, collapsed }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { isTabVisible } = useTabPermissions();
  const { t } = useTranslation();
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [menuExpanded, setMenuExpanded] = useState(true);
  const [managementExpanded, setManagementExpanded] = useState(true);
  const [adminExpanded, setAdminExpanded] = useState(true);

  const userRole = user?.role ?? UserRole.User;
  const approvalStatus = user?.approvalStatus ?? ApprovalStatus.Pending;

  const isAdmin   = userRole === UserRole.Admin;
  const isPM      = userRole === UserRole.PM;
  const isLead    = userRole === UserRole.Lead || userRole === UserRole.PM;
  const isEmployee = userRole === UserRole.Employee || isLead || isAdmin;
  const isApproved = isAdmin || approvalStatus === ApprovalStatus.Approved;

  /* Navigation groups — `tabKey` links each item to the permission system */
  const mainNavItems = [
    { path: '/dashboard',        label: t('sidebar.dashboard'),        icon: LayoutDashboard, tabKey: 'dashboard',        visible: isApproved },
    { path: '/time-entries',     label: t('sidebar.timeEntries'),      icon: Clock,           tabKey: 'time-entries',     visible: isApproved },
    { path: '/my-tasks',         label: t('sidebar.myTasks'),          icon: ListTodo,        tabKey: 'my-tasks',         visible: isApproved },
    { path: '/production-tasks', label: t('sidebar.productionTasks'),  icon: Briefcase,       tabKey: 'production-tasks', visible: isApproved && isEmployee },
  ].filter(i => i.visible && isTabVisible(i.tabKey));

  const managementNavItems = [
    { path: '/sync',             label: t('sidebar.sapSync'),       icon: RefreshCw, tabKey: 'sync',          visible: isApproved },
    { path: '/reports',          label: t('sidebar.reports'),       icon: BarChart3, tabKey: 'reports',       visible: isApproved && (isLead || isAdmin) },
    { path: '/kpi-dashboard',    label: t('sidebar.kpiDashboard'),  icon: Compass,   tabKey: 'kpi-dashboard', visible: isApproved && (isLead || isAdmin) },
    { path: '/kpi-ai-analytics', label: t('sidebar.aiAnalytics'),   icon: Brain,     tabKey: 'kpi-dashboard', visible: isApproved && (isLead || isAdmin) },
    { path: '/employees',        label: t('sidebar.employees'),     icon: Users,     tabKey: 'employees',     visible: isApproved && (isPM || isAdmin) },
    { path: '/team-overview',    label: t('sidebar.teamOverview'),  icon: Eye,       tabKey: 'team-overview', visible: isApproved && (isLead || isAdmin || isPM) },
    { path: '/org-hierarchy',    label: t('sidebar.orgHierarchy'),  icon: Network,   tabKey: 'org-hierarchy', visible: isApproved },
  ].filter(i => i.visible && isTabVisible(i.tabKey));

  const adminNavItems = [
    { path: '/admin/userManagement',    label: t('sidebar.userManagement'),  icon: UserCheck,      tabKey: 'admin-user-management', visible: isAdmin },
    { path: '/admin/teams',             label: t('sidebar.teamManagement'),  icon: ClipboardCheck, tabKey: 'admin-teams',           visible: isAdmin || isPM },
    { path: '/admin/tab-permissions',   label: t('sidebar.tabPermissions'),  icon: Shield,         visible: isAdmin },
    { path: '/admin/ai-insight-emails', label: t('sidebar.aiInsightEmails'), icon: Mail,           visible: isAdmin },
  ].filter(i => i.visible);

  const showTip = (e, label) => {
    if (!collapsed) return;
    const r = e.currentTarget.getBoundingClientRect();
    setActiveTooltip({ label, y: r.top + r.height / 2 });
  };

  /* --- NavItem --- */
  const NavItem = ({ item }) => {
    const Icon = item.icon;
    const isActive =
      location.pathname === item.path ||
      (item.path !== '/dashboard' && location.pathname.startsWith(item.path));

    return (
      <NavLink
        to={item.path}
        onClick={onClose}
        onMouseEnter={e => showTip(e, item.label)}
        onMouseLeave={() => setActiveTooltip(null)}
        className={`
          group relative flex items-center gap-3
          ${collapsed ? 'justify-center w-10 mx-auto px-0' : 'px-3'}
          py-2.5 rounded-xl text-[0.875rem] font-medium
          transition-all duration-150 select-none
          ${isActive
            ? 'bg-gradient-to-r from-operis-blue to-indigo-600 text-white shadow-md shadow-operis-blue/25'
            : 'text-slate-600 dark:text-gray-400 hover:bg-indigo-50/80 hover:text-operis-blue dark:hover:bg-white/[0.07] dark:hover:text-gray-100'
          }
        `}
      >
        {isActive && !collapsed && (
          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-full bg-white/40" />
        )}
        <Icon className={`flex-shrink-0 ${collapsed ? 'w-5 h-5' : 'w-[17px] h-[17px]'} ${!isActive ? 'group-hover:scale-110 transition-transform duration-150' : ''}`} />
        {!collapsed && (
          <span className="min-w-0 flex-1 text-left leading-normal [overflow-wrap:anywhere]">
            {item.label}
          </span>
        )}
        {collapsed && isActive && (
          <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-white/70" />
        )}
      </NavLink>
    );
  };

  /* --- Section label (divider when rail collapsed) --- */
  const SectionDivider = () => (
    <div className="my-2 mx-2 h-px bg-gradient-to-r from-transparent via-slate-300 dark:via-white/10 to-transparent" />
  );

  /** Non-interactive section title (legacy / reuse); use SectionHeader for collapsible groups. */
  const SectionLabel = ({ label }) =>
    collapsed ? (
      <SectionDivider />
    ) : (
      <p className="px-3 pt-1 pb-1.5 text-[10.5px] font-bold text-indigo-400/70 dark:text-gray-600 uppercase tracking-[0.13em] select-none leading-normal">
        {label}
      </p>
    );

  /** Collapsible group header when sidebar is expanded */
  const SectionHeader = ({ label, expanded, onToggle }) => (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-left
        text-[10.5px] font-bold text-indigo-400/70 dark:text-gray-500 uppercase tracking-[0.13em] select-none
        hover:bg-slate-100/90 dark:hover:bg-white/[0.06] transition-colors"
    >
      <span className="leading-normal">{label}</span>
      <ChevronDown
        className={`w-3.5 h-3.5 flex-shrink-0 text-indigo-400/55 dark:text-gray-500 transition-transform duration-200 ${
          expanded ? 'rotate-0' : '-rotate-90'
        }`}
        aria-hidden
      />
    </button>
  );

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside className={`
        fixed lg:sticky top-0 lg:top-16 left-0
        z-50 lg:z-auto
        flex flex-col
        h-screen lg:h-[calc(100vh-4rem)]
        ${collapsed ? 'w-[68px]' : 'w-[240px]'}
        bg-gradient-to-b from-slate-50 via-white to-slate-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900
        border-r border-slate-200/80 dark:border-gray-700
        shadow-[1px_0_8px_-2px_rgba(0,0,0,0.06)] dark:shadow-none
        transition-[width,transform] duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        overflow-hidden
      `}>

        {/* Mobile header */}
        <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-slate-200/80 dark:border-gray-700 flex-shrink-0">
          <span className="font-extrabold text-sm tracking-[0.2em] text-operis-blue dark:text-indigo-300">OPERIS</span>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable nav */}
        <div
          className={`flex-1 overflow-y-auto overflow-x-hidden py-4 flex flex-col gap-0.5 ${collapsed ? 'px-1' : 'px-2'}`}
          style={{ scrollbarWidth: 'none' }}
        >
          {/* Pending approval */}
          {!isApproved && !isAdmin && (
            collapsed ? (
              <div className="mb-3 flex justify-center" title={t('sidebar.accountPendingApproval')}>
                <AlertCircle className="w-5 h-5 text-amber-500" />
              </div>
            ) : (
              <div className="mb-3 mx-1 px-3 py-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-xl">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-amber-700 dark:text-amber-400">{t('sidebar.pendingApprovalTitle')}</p>
                    <p className="text-[11px] text-amber-600/80 dark:text-amber-500/80 mt-0.5 leading-relaxed">
                      {t('sidebar.pendingApprovalBody')}
                    </p>
                  </div>
                </div>
              </div>
            )
          )}

          {/* Main */}
          {mainNavItems.length > 0 && (
            <div className="space-y-0.5">
              {collapsed ? (
                <>
                  <SectionDivider />
                  {mainNavItems.map(item => <NavItem key={item.path} item={item} />)}
                </>
              ) : (
                <>
                  <SectionHeader label={t('sidebar.menu')} expanded={menuExpanded} onToggle={() => setMenuExpanded(v => !v)} />
                  {menuExpanded && mainNavItems.map(item => <NavItem key={item.path} item={item} />)}
                </>
              )}
            </div>
          )}

          {/* Management */}
          {managementNavItems.length > 0 && (
            <div className="mt-4 space-y-0.5">
              {collapsed ? (
                <>
                  <SectionDivider />
                  {managementNavItems.map(item => <NavItem key={item.path} item={item} />)}
                </>
              ) : (
                <>
                  <SectionHeader
                    label={t('sidebar.management')}
                    expanded={managementExpanded}
                    onToggle={() => setManagementExpanded(v => !v)}
                  />
                  {managementExpanded && managementNavItems.map(item => <NavItem key={item.path} item={item} />)}
                </>
              )}
            </div>
          )}

          {/* Admin */}
          {adminNavItems.length > 0 && (
            <div className="mt-4 space-y-0.5">
              {collapsed ? (
                <>
                  <SectionDivider />
                  {adminNavItems.map(item => <NavItem key={item.path} item={item} />)}
                </>
              ) : (
                <>
                  <SectionHeader label={t('sidebar.admin')} expanded={adminExpanded} onToggle={() => setAdminExpanded(v => !v)} />
                  {adminExpanded && adminNavItems.map(item => <NavItem key={item.path} item={item} />)}
                </>
              )}
            </div>
          )}

          {/* Push help to bottom */}
          <div className="flex-1 min-h-[1rem]" />

          <div className="space-y-0.5">
            <NavLink
              to="/onboarding?revisit=true"
              onMouseEnter={(e) => {
                if (!collapsed) return;
                const rect = e.currentTarget.getBoundingClientRect();
                setActiveTooltip({ label: t('sidebar.appTour'), y: rect.top + rect.height / 2 });
              }}
              onMouseLeave={() => setActiveTooltip(null)}
              className={`relative flex items-center ${collapsed ? 'justify-center px-0 mx-1' : 'px-4'} py-3 rounded-xl
                          text-gray-500 dark:text-gray-400
                          hover:bg-gray-100/80 dark:hover:bg-white/5
                          hover:text-operis-blue dark:hover:text-white
                          transition-all duration-200 group`}
            >
              <Compass className="w-5 h-5 flex-shrink-0 transition-all duration-200 group-hover:scale-110 group-hover:rotate-3" />
              {!collapsed && <span className="ml-3 font-medium text-[0.9rem]">{t('sidebar.appTour')}</span>}
            </NavLink>

            <NavLink
              to="/help"
              onMouseEnter={(e) => {
                if (!collapsed) return;
                const rect = e.currentTarget.getBoundingClientRect();
                setActiveTooltip({ label: t('sidebar.helpSupport'), y: rect.top + rect.height / 2 });
              }}
              onMouseLeave={() => setActiveTooltip(null)}
              className={`relative flex items-center ${collapsed ? 'justify-center px-0 mx-1' : 'px-4'} py-3 rounded-xl
                          text-gray-500 dark:text-gray-400
                          hover:bg-gray-100/80 dark:hover:bg-white/5
                          hover:text-operis-blue dark:hover:text-white
                          transition-all duration-200 group`}
            >
              <HelpCircle className="w-5 h-5 flex-shrink-0 transition-all duration-200 group-hover:scale-110 group-hover:rotate-3" />
              {!collapsed && <span className="ml-3 font-medium text-[0.9rem]">{t('sidebar.helpSupport')}</span>}
            </NavLink>
          </div>

        </div>
      </aside>

      {/* Tooltip for collapsed state */}
      {collapsed && activeTooltip && (
        <div
          className="pointer-events-none fixed z-[9999] px-2.5 py-1.5 bg-gray-900 dark:bg-gray-700 text-white text-[12px] font-medium rounded-lg shadow-lg whitespace-nowrap"
          style={{ left: 76, top: activeTooltip.y, transform: 'translateY(-50%)' }}
        >
          {activeTooltip.label}
          <span className="absolute top-1/2 -left-[5px] -translate-y-1/2 border-[5px] border-transparent border-r-gray-900 dark:border-r-gray-700" />
        </div>
      )}
    </>
  );
};

export default Sidebar;
